import { FormattedMessage, useIntl } from "react-intl";
import { UsageChartWrapper, type BasicUsageChartProps } from "./UsageChartWrapper.js";
import { TIERED_PRODUCTS, TieredPriceProductId } from "@project/common";
import { useMemoizedBarSeries } from "../../../hooks/chart.js";
import { IconLayersSubtract } from "@tabler/icons-react";
import { EChartTimeseries } from "../../../components/EChartTimeseries.js";
import { useCustomTilesetUsageSum } from "./hooks/fetchSums.js";
import { checkIsApproachingLimit } from "./utils/limits.js";
import { useCustomTilesetsUsage } from "./hooks/fetchUsages.js";
import { resolveUsageEventDeltas } from "./utils/usageEventsProcessors.js";
import { Text } from "@mantine/core";
import { useProductNames } from "./hooks/productLabels.js";

export const CustomTilesetsUsageChart = ({ from, to, interval, addToDetailedView }: BasicUsageChartProps) => {
  const {
    customTilesetsUsage,
    isLoadingTilesets: isLoadingTilesetsHist,
    isErrorTilesets: isErrorTilesetsHist,
  } = useCustomTilesetsUsage({ from, to, interval });

  const {
    usageSum,
    isLoadingTilesets: isLoadingTilesetsSum,
    isErrorTilesets: isErrorTilesetsSum,
  } = useCustomTilesetUsageSum({ to });

  const intl = useIntl();
  const getName = useProductNames();
  const title = getName(TieredPriceProductId.TieredCustomTileset);

  const series = useMemoizedBarSeries(
    resolveUsageEventDeltas(customTilesetsUsage, usageSum),
    title,
    TIERED_PRODUCTS[TieredPriceProductId.TieredCustomTileset].freeTierAllowance,
    intl.formatMessage({ defaultMessage: "1 tileset free", id: "tileset.allowance" }),
  );

  return (
    <UsageChartWrapper
      title={title}
      iconColor="info.2"
      icon={<IconLayersSubtract size={20} />}
      isLoading={isLoadingTilesetsHist || isLoadingTilesetsSum}
      hasError={isErrorTilesetsHist || isErrorTilesetsSum}
      hasUsage={customTilesetsUsage.length > 0}
      addToDetailedView={addToDetailedView}
      isApproachingLimit={checkIsApproachingLimit(
        usageSum,
        TieredPriceProductId.TieredCustomTileset,
      )}
    >
      <EChartTimeseries series={series} />
      <Text mt="sm">
        <FormattedMessage
          defaultMessage="Active custom tilesets: {usageSum}"
          values={{ usageSum }}
          id="customTilesets.sum"
        />
      </Text>
    </UsageChartWrapper>
  );
};
